import { ApolloProvider } from '@apollo/client';
import { cache } from '@emotion/css';
import { CacheProvider, Global, ThemeProvider } from '@emotion/react';
import { Locale } from '@landsbankinn/l16n';
import { PrismicContextProvider, useApollo } from '@landsbankinn/prismic';
import { LayoutProvider } from '@landsbankinn/slices/components/contexts/LayoutContext';
import { FooterContainer } from '@landsbankinn/slices/containers/FooterContainer';
import { fonts, reset, base, theme } from '@landsbankinn/ui';
import { AcceptedCookies } from '@landsbankinn/utils';
import * as Sentry from '@sentry/browser';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Router from 'next/router';
import React from 'react';
import { CookieConsent } from 'components/cookie-consent/CookieConsent';
import { EditButton } from 'components/edit-button/EditButton';
import { PageTransition } from 'components/page-transition/PageTransition';
import { useRouterScrollTop } from 'hooks/useRouterScrollTop';
import 'intersection-observer';
import { Config } from 'lib/config';
import { FacebookPixel } from 'lib/facebook-pixel';
import type { AllPagesPage } from 'lib/linkResolver';
import { linkResolver, setPages, urlResolver } from 'lib/linkResolver';

import { AppLayout } from '../components/app-layout/AppLayout';
import data from '../lib/prismicIntrospect.json';

export interface LBPageProps {
  preview?: boolean | null;
  previewData?: {
    ref?: string;
  };
  initialApolloState?: Record<string, unknown>;
  standalone?: boolean;
  pages: AllPagesPage[];
  page: AllPagesPage;
  website?: any;
  lang: string;
}

interface LBAppProps extends AppProps<LBPageProps> {
  pageProps: LBPageProps;
}

if (Config.SENTRY_DSN && Config.SENTRY_DSN !== '') {
  Sentry.init({
    dsn: Config.SENTRY_DSN,
  });
}

if (typeof window !== 'undefined' && typeof document !== undefined) {
  document.addEventListener('copy', function (e) {
    const text = window
      ?.getSelection?.()
      ?.toString()
      ?.replace(/(&shy;|­|&#173;)/gi, '');
    e?.clipboardData?.setData('text/plain', text ?? '');
    e.preventDefault();
  });
}

function getActiveIds(pages: any, active: any) {
  if (!active) return [];
  const ids = [active.id];
  let current = active;
  while (current?.parentId) {
    current = pages.find((p: any) => p.id == current.parentId);
    if (current) {
      ids.push(current.id);
    }
  }
  return ids.reverse();
}

Router.events.on('routeChangeComplete', (url) => {
  FacebookPixel.pageView();
});

export default function LbApp({ Component, pageProps, router }: LBAppProps) {
  const { preview, previewData, pages, initialApolloState, standalone } =
    pageProps;

  useRouterScrollTop();

  const client = useApollo({
    introspectionQueryResultData: data,
    initialState: initialApolloState,
    prismicRef: preview ? previewData?.ref : null,
  });

  if (standalone) {
    return <Component {...pageProps} />;
  }

  const locale = pageProps?.website?._meta?.lang || 'is';
  const [lang] = locale.split('-');
  const metaLocale = !!lang ? `${lang}_${lang?.toUpperCase()}` : 'is_IS';
  const activeIds = getActiveIds(pages, pageProps.page);
  const url = router?.asPath || '';

  setPages(pages, lang);

  return (
    <ApolloProvider client={client}>
      <Locale initialLocale={lang}>
        <CacheProvider value={cache}>
          <AcceptedCookies>
            <ThemeProvider theme={theme}>
              <LayoutProvider>
                <PrismicContextProvider
                  linkResolver={linkResolver}
                  urlResolver={urlResolver}
                >
                  <Global styles={(props) => [fonts, reset, base(props)]} />
                  <Head>
                    <meta
                      property="creationtimestamp"
                      content={new Date().toString()}
                    ></meta>
                    <meta property="og:locale" content={metaLocale}></meta>
                    <meta
                      property="og:url"
                      content={`https://arsskyrsla.landsbankinn.is${url}`}
                    ></meta>
                  </Head>
                  <AppLayout page={pageProps} activeIds={activeIds}>
                    <PageTransition router={router}>
                      <>
                        <Component {...pageProps} />
                        <FooterContainer
                          text={pageProps?.website?.text}
                          shortcuts={pageProps?.website?.shortcuts}
                          socialMedia={pageProps?.website?.social_media}
                          group={pageProps?.website?.body}
                        />
                      </>
                    </PageTransition>
                    <CookieConsent />
                  </AppLayout>
                  <div id="portal" />

                  {preview && (
                    <script
                      async
                      defer
                      src="//static.cdn.prismic.io/prismic.js?repo=arsskyrsla&amp;new=true"
                    />
                  )}
                  <EditButton />
                </PrismicContextProvider>
              </LayoutProvider>
            </ThemeProvider>
          </AcceptedCookies>
        </CacheProvider>
      </Locale>
    </ApolloProvider>
  );
}
